// Inline markdown renderer for assistant prose and insight bullets.
//
// Scope: what the model actually emits in practice. Block-level: paragraphs,
// pipe tables, bullet lists, headings (### only), horizontal rules (---).
// Inline: **bold**, *italic*, `code`, and explicit \n breaks within a
// paragraph. Plus an auto-highlight pass that bolds numbers, percentages,
// currency, and dates in plain text so the eye can scan results quickly
// even when the model forgets to add **bold** markers itself.
//
// Why hand-rolled instead of react-markdown + remark-gfm: ~50KB gzipped
// for the dep, and we'd lose the auto-highlight pass.
//
// `streaming` skips the auto-highlight pass — the regex is O(N) per chunk
// and makes long streamed answers feel jerky. Block parsing is cheap.
import { Fragment, type ReactNode } from "react";

interface Props {
  text: string;
  className?: string;
  streaming?: boolean;
}

export function Markdown({ text, className, streaming }: Props) {
  const blocks = parseBlocks(text);
  return (
    <div className={className}>
      {blocks.map((b, i) => renderBlock(b, i, !!streaming))}
    </div>
  );
}

export function MarkdownInline({ text }: { text: string }) {
  return <>{renderInline(text, false)}</>;
}

/* ------------------------------------------------------------------ */
/* Block-level parsing                                                  */
/* ------------------------------------------------------------------ */

type Block =
  | { kind: "paragraph"; text: string }
  | { kind: "heading"; level: 1 | 2 | 3; text: string }
  | { kind: "list"; ordered: boolean; items: string[] }
  | { kind: "table"; header: string[]; rows: string[][] }
  | { kind: "hr" };

// Pipe-table separator row: only |, -, :, and spaces. At least one dash.
const TABLE_SEP_RE = /^\s*\|?(?:\s*:?-+:?\s*\|)+\s*:?-+:?\s*\|?\s*$/;
const TABLE_LINE_RE = /^\s*\|.*\|\s*$/;
const HEADING_RE = /^(#{1,3})\s+(.+)$/;
const BULLET_RE = /^\s*[-*]\s+(.+)$/;
const ORDERED_RE = /^\s*\d+\.\s+(.+)$/;
const HR_RE = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/;

// Line-driven parser: walks the input one line at a time and emits blocks
// as their patterns are recognized. Lets the model mix headings, bullets,
// and paragraphs without blank lines between them (which it does in
// practice), and emits horizontal rules on stand-alone `---` lines.
function parseBlocks(text: string): Block[] {
  const out: Block[] = [];
  const lines = text.split("\n");
  let paragraphBuf: string[] = [];

  const flushParagraph = () => {
    if (paragraphBuf.length === 0) return;
    const joined = paragraphBuf.join("\n").replace(/\n+$/, "");
    if (joined.length > 0) out.push({ kind: "paragraph", text: joined });
    paragraphBuf = [];
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Blank line ends the current paragraph but doesn't itself emit a block.
    if (line.trim() === "") {
      flushParagraph();
      i++;
      continue;
    }

    // Horizontal rule: --- / *** / ___ on its own line.
    if (HR_RE.test(line)) {
      flushParagraph();
      out.push({ kind: "hr" });
      i++;
      continue;
    }

    // Heading: ### text. Single line; bullets / paragraphs may follow
    // directly with no blank line in between.
    const hMatch = line.match(HEADING_RE);
    if (hMatch) {
      flushParagraph();
      const level = Math.min(3, hMatch[1].length) as 1 | 2 | 3;
      out.push({ kind: "heading", level, text: hMatch[2] });
      i++;
      continue;
    }

    // Bullet list: consume consecutive bullet lines.
    if (BULLET_RE.test(line)) {
      flushParagraph();
      const items: string[] = [];
      while (i < lines.length && BULLET_RE.test(lines[i])) {
        items.push((lines[i].match(BULLET_RE) as RegExpMatchArray)[1]);
        i++;
      }
      out.push({ kind: "list", ordered: false, items });
      continue;
    }

    // Ordered list: `1.` / `2.` / … Consume consecutive numbered lines.
    if (ORDERED_RE.test(line)) {
      flushParagraph();
      const items: string[] = [];
      while (i < lines.length && ORDERED_RE.test(lines[i])) {
        items.push((lines[i].match(ORDERED_RE) as RegExpMatchArray)[1]);
        i++;
      }
      out.push({ kind: "list", ordered: true, items });
      continue;
    }

    // Pipe table: header line + separator line, then any number of body
    // rows. Lookahead at line i+1 keeps us from gobbling a stray `|foo|`
    // line that isn't actually starting a table.
    if (
      TABLE_LINE_RE.test(line) &&
      i + 1 < lines.length &&
      TABLE_SEP_RE.test(lines[i + 1])
    ) {
      flushParagraph();
      const header = splitTableRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && TABLE_LINE_RE.test(lines[i])) {
        rows.push(splitTableRow(lines[i]));
        i++;
      }
      out.push({ kind: "table", header, rows });
      continue;
    }

    // Anything else accumulates into the current paragraph; flushed
    // when we hit a blank line, a different block kind, or EOF.
    paragraphBuf.push(line);
    i++;
  }
  flushParagraph();
  return out;
}

function splitTableRow(line: string): string[] {
  // Strip the leading and trailing pipe, then split on remaining pipes.
  // Pad whitespace on each cell so "| a |b |" renders cleanly.
  const trimmed = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  return trimmed.split("|").map((s) => s.trim());
}

/* ------------------------------------------------------------------ */
/* Block rendering                                                      */
/* ------------------------------------------------------------------ */

function renderBlock(block: Block, key: number, streaming: boolean): ReactNode {
  switch (block.kind) {
    case "heading": {
      const top = key > 0 ? "mt-4" : "";
      const cls =
        block.level === 1
          ? `${top} text-[17px] font-semibold text-neutral-900 leading-tight`
          : block.level === 2
            ? `${top} text-[15px] font-semibold text-neutral-900 leading-tight`
            : `${top} text-[14px] font-semibold text-neutral-800 leading-tight`;
      const HTag = (`h${block.level}` as unknown) as "h1" | "h2" | "h3";
      return (
        <HTag key={key} className={cls}>
          {renderInline(block.text, streaming)}
        </HTag>
      );
    }
    case "list": {
      const ListTag = block.ordered ? "ol" : "ul";
      const listCls = block.ordered ? "list-decimal" : "list-disc";
      return (
        <ListTag key={key} className={`${key > 0 ? "mt-3" : ""} ${listCls} pl-5 space-y-1`}>
          {block.items.map((item, i) => (
            <li key={i}>{renderInline(item, streaming)}</li>
          ))}
        </ListTag>
      );
    }
    case "table":
      return (
        <div key={key} className={`${key > 0 ? "mt-3" : ""} overflow-x-auto`}>
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-line">
                {block.header.map((h, i) => (
                  <th key={i} className="text-left font-semibold text-neutral-900 px-2 py-1.5">
                    {renderInline(h, streaming)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r} className="border-b border-line/70 last:border-b-0">
                  {row.map((cell, c) => (
                    <td key={c} className="px-2 py-1.5 align-top text-neutral-800">
                      {renderInline(cell, streaming)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "hr":
      return <hr key={key} className={`${key > 0 ? "mt-4" : ""} border-t border-line`} />;
    case "paragraph":
    default:
      return (
        <p key={key} className={key > 0 ? "mt-3" : ""}>
          {renderInline(block.text, streaming)}
        </p>
      );
  }
}

/* ------------------------------------------------------------------ */
/* Inline rendering — unchanged from before block-level support added.  */
/* ------------------------------------------------------------------ */

// Explicit markdown tokens the model emits. Split here first; the
// highlight pass below only runs on the plain-text segments so we
// don't double-process already-bolded spans.
const TOKEN = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*\n]+\*|\n)/g;

function renderInline(text: string, streaming: boolean): ReactNode {
  const out: ReactNode[] = [];
  const parts = text.split(TOKEN);
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (!part) continue;
    if (part === "\n") {
      out.push(<br key={`b${i}`} />);
    } else if (part.startsWith("`") && part.endsWith("`")) {
      out.push(
        <code key={`c${i}`} className="font-mono text-[13px] bg-chip px-1 py-0.5 rounded">
          {part.slice(1, -1)}
        </code>,
      );
    } else if (part.startsWith("**") && part.endsWith("**")) {
      out.push(
        <strong key={`s${i}`} className="font-semibold text-neutral-900">
          {part.slice(2, -2)}
        </strong>,
      );
    } else if (part.startsWith("*") && part.endsWith("*")) {
      out.push(
        <em key={`e${i}`} className="italic">
          {part.slice(1, -1)}
        </em>,
      );
    } else if (streaming) {
      // Streaming: skip the O(N) auto-highlight regex per chunk so long
      // answers stay smooth. AnswerBody re-renders without `streaming`
      // once the turn closes and picks up currency/date/percentage bolds.
      out.push(<Fragment key={`p${i}`}>{part}</Fragment>);
    } else {
      // Plain text → auto-highlight pass.
      out.push(...renderHighlights(part, i));
    }
  }
  return out;
}

// Auto-bold pass. Each capture group below corresponds to one kind of
// highlight — keep them in priority order (longer / more specific first
// so e.g. "$1,200" matches as currency, not as "1,200" plus a stray $).
const HIGHLIGHT = new RegExp(
  [
    // Currency: $1,200 / $1.2M / $1.5K
    "(\\$\\d[\\d,]*(?:\\.\\d+)?[KMB]?)",
    // Percentages: 38% / 0.5% / -12.4%
    "(-?\\d+(?:\\.\\d+)?%)",
    // ISO date: 2026-04-28 (formatted to "Apr 28" / "Apr 28, 2026" inline)
    "(\\b\\d{4}-\\d{2}-\\d{2}\\b)",
    // Big numbers with thousands separator: 1,296 / 12,345.67
    "(\\b\\d{1,3}(?:,\\d{3})+(?:\\.\\d+)?\\b)",
  ].join("|"),
  "g",
);

function renderHighlights(text: string, parentKey: number): ReactNode[] {
  const out: ReactNode[] = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  // Clone the regex so we don't mutate the shared lastIndex across calls.
  const re = new RegExp(HIGHLIGHT.source, HIGHLIGHT.flags);
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    const before = text.slice(lastIndex, m.index);
    if (before) out.push(<Fragment key={`t${parentKey}-${i}`}>{before}</Fragment>);
    out.push(
      <strong key={`h${parentKey}-${i}`} className="font-semibold text-neutral-900">
        {prettify(m[0])}
      </strong>,
    );
    lastIndex = m.index + m[0].length;
    i++;
  }
  const tail = text.slice(lastIndex);
  if (tail) out.push(<Fragment key={`t${parentKey}-tail`}>{tail}</Fragment>);
  return out;
}

// Reformat ISO dates inline so "2026-04-28" reads as "Apr 28".
// Other matches pass through unchanged.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
function prettify(token: string): string {
  if (ISO_DATE.test(token)) {
    const d = new Date(token);
    if (!Number.isNaN(d.getTime())) {
      // Same-year heuristic isn't available in this scope; keep the
      // year for inline mentions so the date is unambiguous outside
      // chart-axis context.
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }
  }
  return token;
}
